import { authenticatedProApiFetch } from './pro-api.client';

export type ResolvedDelivery = 'summary' | 'separate' | 'off';

export interface NotificationDeliverySettings {
  paused: boolean;
  immediateMinSeverity: number | null;
  summaryIntervalMinutes: number;
  resolvedDelivery: ResolvedDelivery;
}

export type ReportChannel = 'email' | 'sms';

export interface ReportDuration {
  ms: number;
  productName: string;
  alertName: string;
  channel?: ReportChannel;
  at: string;
}

export interface ReportCadence {
  avgMs: number;
  minMs: number;
  maxMs: number;
}

export interface ReportChannelTotals {
  considered: number;
  sent: number;
  held: number;
  blocked: number;
  batchedTogether: number;
  suppression: Record<string, number>;
  sentBreakdown: { new: number; batched: number; summary: number; resolved: number };
}

export interface NotificationReportProduct {
  id: string;
  name: string;
  alertEvents: number;
  notifications: Record<ReportChannel, number>;
  summaries: Record<ReportChannel, number>;
  alerts: Array<{
    code: string;
    name: string;
    events: number;
    flaps: number;
    episodes: number;
    notifications: Record<ReportChannel, number>;
    cadence: Partial<Record<ReportChannel, ReportCadence>>;
    status: 'active' | 'cleared' | 'unknown';
  }>;
  timeline: {
    alerts: string[];
    sent: Array<{ at: string; channel: ReportChannel; kind: string }>;
  };
}

export interface NotificationReport {
  user: { id: string; email: string | null; name: string };
  subscribedProducts: number;
  range: { start: string; end: string };
  totals: {
    alertEvents: number;
    alertReports: number;
    episodes: number;
    flaps: number;
    channels: Record<ReportChannel, ReportChannelTotals>;
  };
  metrics: {
    longestWait: ReportDuration | null;
    fastestRepeat: ReportDuration | null;
  };
  products: NotificationReportProduct[];
}

export function fetchDeliverySettings(token: string, userId: string) {
  return authenticatedProApiFetch<NotificationDeliverySettings>(
    `/notification-delivery/settings/${userId}`,
    token,
  );
}

export function saveDeliverySettings(
  token: string,
  userId: string,
  settings: NotificationDeliverySettings,
) {
  return authenticatedProApiFetch<NotificationDeliverySettings>(
    `/notification-delivery/settings/${userId}`,
    token,
    { method: 'PUT', body: JSON.stringify(settings) },
  );
}

export function fetchNotificationReport(
  token: string,
  userId: string,
  range: { start: string; end: string },
) {
  const qs = new URLSearchParams(range);
  return authenticatedProApiFetch<NotificationReport>(
    `/notification-delivery/report/${userId}?${qs.toString()}`,
    token,
  );
}
